<template>
    <div v-for="dropdown in dropdowns" :key="dropdown"
        class="dropdown"
        :class="{ 'chat' : dropdown.name == 'CHAT'}">

        <!-- Trigger: bell (general) / chat glyph, with the unread-count badge -->
        <button :ref="'notificationButton_' + dropdown.name"
            class="userButton"
            :aria-label="dropdown.name == 'CHAT' ? 'Conversaciones' : 'Notificaciones'"
            @click="NotificationButtonHandler(dropdown)"
            data-bs-toggle="dropdown" data-bs-auto-close="outside" data-bs-display="static" aria-expanded="false">

            <div v-if="dropdown.notificationsCount > 0" class="notification" :key="dropdown.pulseKey">
                {{ FormatCount(dropdown.notificationsCount) }}
            </div>

            <i :class="{'bi bi-chat-dots-fill' : dropdown.name == 'CHAT', 'bi bi-bell-fill' : dropdown.name == 'OTHERS'}"></i>
        </button>

        <div class="dropdown-menu notificationMenu staticDropdownPanel">
            <!-- Header: back to categories · current title · mark-as-read -->
            <div class="nm-header">
                <div class="nm-header__row">
                    <button v-if="dropdown.name != 'CHAT' && selectedCategoryPK != null"
                        class="nm-iconBtn nm-header__back" aria-label="Volver al centro de notificaciones"
                        title="Volver al centro de notificaciones"
                        @click="BackToCategoriesClickHandler">
                        <i class="bi bi-arrow-left"></i>
                    </button>

                    <b class="nm-header__title">{{ dropdown.name == 'CHAT' ? 'Conversaciones' : selectedCategoryPK == null ? 'Centro de notificaciones' :
                        notificationCategories.find(x => x.PKNotificationCategory == selectedCategoryPK).DisplayText }}</b>

                    <button v-if="dropdown.name == 'CHAT' || selectedCategoryPK != null"
                        class="nm-iconBtn nm-header__clear" aria-label="Marcar todas como leídas"
                        title="Marcar todas como leídas"
                        @click="ClearNotificationsByCategory(dropdown)">
                        <i class="bi bi-envelope-open-fill"></i>
                    </button>
                </div>
            </div>

            <!-- Category picker (general dropdown only) -->
            <div v-if="dropdown.name !== 'CHAT'" class="nm-categories" :style="{ display: selectedCategoryPK == null ? '' : 'none' }">
                <!-- Skeleton rows while the categories request is in flight -->
                <template v-if="categoriesLoading">
                    <div v-for="n in 5" :key="'csk' + n" class="nm-catSkeleton" aria-hidden="true">
                        <div class="nm-catSkeleton__icon"></div>
                        <div class="nm-catSkeleton__label"></div>
                    </div>
                </template>

                <!-- Loaded: each row eases up into place, staggered -->
                <template v-else>
                    <button v-for="(category, i) in notificationCategories.filter(x => x.Category != 'CHAT')" :key="category.PKNotificationCategory"
                        class="nm-category nm-category--enter"
                        :style="{ '--nm-enter-delay': (i * 45) + 'ms' }"
                        @click="ChangeNotificationCategory(category)">
                        <span class="nm-category__icon"><i :class="category.IconClass"></i></span>
                        <span class="nm-category__label">{{ category.DisplayText }}</span>
                        <span v-if="category.NotificationsCount > 0" class="nm-category__count">{{ FormatCount(category.NotificationsCount) }}</span>
                        <i class="bi bi-chevron-right nm-category__chevron"></i>
                    </button>
                </template>
            </div>

            <!-- Type filter for the opened category -->
            <div :style="{ display: dropdown.name != 'CHAT' && selectedCategoryPK == null ? 'none' : '' }">
                <!-- Chat: skeleton chips while the types load (no category exists yet to render) -->
                <div v-if="dropdown.name == 'CHAT' && categoriesLoading" class="nm-summary" aria-hidden="true">
                    <div class="nm-summary__title">Resumen de actualizaciones</div>
                    <div class="nm-chips">
                        <div v-for="n in 3" :key="'chipsk' + n" class="nm-chipSkeleton"></div>
                    </div>
                </div>

                <div v-for="category in notificationCategories" :key="category.PKNotificationCategory"
                    class="nm-summary"
                    :style="{ display: ShowCategorySummary(category, dropdown) ? '' : 'none' }">

                    <div class="nm-summary__title">Resumen de actualizaciones</div>

                    <div class="nm-chips">
                        <button v-for="(notificationType, i) in category.NotificationTypes" :key="notificationType.PKNotificationType"
                            class="nm-chip nm-chip--enter"
                            :style="{ '--nm-enter-delay': (i * 40) + 'ms' }"
                            :class="{ 'nm-chip--selected' : notificationType.PKNotificationType == category.selectedTypePK }"
                            @click="NotificationTypeChange(category.PKNotificationCategory, notificationType.PKNotificationType)">
                            <span class="nm-chip__icon"><i :class="notificationType.IconName"></i></span>
                            <span class="nm-chip__label">{{ FormatCount(notificationType.Count) + ' ' + notificationType.FilterText }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Chat: skeleton rows while categories load (no CHAT category exists yet to render) -->
            <div v-if="dropdown.name == 'CHAT' && categoriesLoading" class="notificationsWrapper nm-skeletonList" aria-hidden="true">
                <div v-for="n in 5" :key="'chatsk' + n" class="nm-skeleton">
                    <div class="nm-skeleton__avatar"></div>
                    <div class="nm-skeleton__lines">
                        <div class="nm-skeleton__line nm-skeleton__line--sm"></div>
                        <div class="nm-skeleton__line nm-skeleton__line--lg"></div>
                        <div class="nm-skeleton__line nm-skeleton__line--xs"></div>
                    </div>
                </div>
            </div>

            <!-- Notifications list -->
            <div v-for="notificationCategory in (dropdown.name == 'CHAT' ? notificationCategories.filter(x => x.Category === 'CHAT') : notificationCategories.filter(x => x.Category !== 'CHAT'))"
                :id="'notificationCategory_' + notificationCategory.PKNotificationCategory"
                :key="notificationCategory.PKNotificationCategory"
                :style="{ display: dropdown.name == 'CHAT' || selectedCategoryPK == notificationCategory.PKNotificationCategory ? '' : 'none' }"
                class="notificationsWrapper">

                <NotificationCard v-for="notification in GetVisibleNotifications(notificationCategory)"
                    :key="notification"
                    :notification="notification"
                    :userData="store.loadedUsersData[notification.FKUser]"
                    @click="ClickHandler(notificationCategory, notification)"/>

                <!-- Initial-load skeletons: a shaped placeholder for the first page -->
                <div v-if="IsInitialLoading(notificationCategory)" class="nm-skeletonList" aria-hidden="true">
                    <div v-for="n in 4" :key="'sk' + n" class="nm-skeleton">
                        <div class="nm-skeleton__avatar"></div>
                        <div class="nm-skeleton__lines">
                            <div class="nm-skeleton__line nm-skeleton__line--sm"></div>
                            <div class="nm-skeleton__line nm-skeleton__line--lg"></div>
                            <div class="nm-skeleton__line nm-skeleton__line--xs"></div>
                        </div>
                    </div>
                </div>

                <div class="notificationLoaderWrapper"
                    :style="{ display: dropdown.name == 'CHAT' ||
                        selectedCategoryPK == notificationCategory.PKNotificationCategory ? 'flex' : 'none' }">

                    <div :ref="'notificationLoader_' + notificationCategory.PKNotificationCategory" class="notificationLoader"></div>

                    <div v-if="ShowSpinnerFor(notificationCategory)" class="nm-spinner">
                        <span></span><span></span><span></span>
                    </div>

                    <div v-if="ShowEndLabelFor(notificationCategory)" class="nm-empty">
                        <template v-if="HasVisibleNotificationsFor(notificationCategory)">
                            <span class="nm-empty__rule"></span>
                            <span class="nm-empty__text">Fin de las notificaciones</span>
                        </template>
                        <template v-else>
                            <span class="nm-empty__icon"><i class="bi bi-bell-slash"></i></span>
                            <span class="nm-empty__title">Todo al día</span>
                            <span class="nm-empty__hint">No hay notificaciones para mostrar aquí.</span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import NotificationService from '@/services/NotificationService.js';
import UserService from '@/services/UserService.js';
import { useRouter } from 'vue-router';
import { useStore } from '@/store/DefaultStore';
import { collection, onSnapshot, orderBy, query, limit } from "firebase/firestore"; 
import { db } from '@/authentication/firebase.js'
import { onUnmounted } from 'vue';
import PromiseCallerService from '@/utilities/PromiseCallerService';
import ShowNotification from '@/services/ToastService.js';
import NotificationCard from '@/components/Generic/NotificationCard.vue';
import EventBus from '@/utilities/EventBus';

export default{
    data(){
        return{
            firstNotification: true,
            firstMessageNotification: true,
            firstNotificationsUpdate: true,

            // True while the categories request is in flight — drives the
            // panel-level skeletons (category picker / chat list) before any
            // real category exists to render.
            categoriesLoading: true,

            users: [],
            notificationCategories: [],

            selectedCategoryPK: null,
            lastOtherSelectedCategoryPKPK: null,
            chatCategoryPK: null,

            dropdowns: [{
                name: 'CHAT',
                notificationsCount: 0,
                pulseKey: 0
            },
            {
                name: 'OTHERS',
                notificationsCount: 0,
                pulseKey: 0
            }]
        }
    },

    components: {
        NotificationCard
    },

    computed: {
        // Wrapped as computed (per-item getters, since v-for items can't be
        // passed to a plain no-arg computed) so the chained/multi-call v-if
        // conditions in the template resolve to a single named lookup.
        ShowSpinnerFor(){
            return category => this.ShowSpinner(category) && !this.IsInitialLoading(category);
        },

        ShowEndLabelFor(){
            return category => this.ShowEndLabel(category);
        },

        HasVisibleNotificationsFor(){
            return category => this.GetVisibleNotifications(category).length > 0;
        }
    },

    setup(){
        const router = useRouter();
        const store = useStore();

        return{
            router, store
        }
    },

    async mounted(){
        this.GetNotificationCategories();

        //Cuando se recibe una nueva notificación
        let channel = 'organizationsNotificationsUpdate_' + this.store.CurrentOrganizationPK;
        if(process.env.VUE_APP_ENV != 'PRD')
            channel = process.env.VUE_APP_ENV + '_' + channel;

        const q = query(
            collection(db, channel),
            orderBy('PKNotification', 'desc'),
            limit(1)
        );

        const unsub = onSnapshot(q, snapshot => {
            if(this.firstNotification){
                this.firstNotification = false;
                return;
            }

            let PKNotification = snapshot.docs[0].data().PKNotification;

            //Se obtienen los detalles de la notificación
            NotificationService.GetNotification({PKNotification})
            .then(async data => {
                let notification = data.Notification;

               //Si la notificacion no esta destinada para el usuario
               if(notification == null){
                    return;
                }

                //Se obtiene la información del usuario
                await this.GetNewUsersData([notification]);
                
                let category = this.notificationCategories.find(x => x.PKNotificationCategory == data.Notification.PKNotificationCategory);
                let allCategory = this.notificationCategories.find(x => x.Category == 'ALL');

                //Agrega la notificación a la lista de notificaciones de esa categoria
                category.notifications.unshift(notification);
                //Agrega la notificacion a la lista de todas
                allCategory.notifications.unshift(notification);

                //Se agrega 1 al coteo de notificaciones de esa categoría
                category.NotificationsCount++;

                //se agrega 1 al conteo de ALL (solo si la notificacion no es de tipo chat)
                if(data.Notification.Category != 'CHAT'){
                    this.notificationCategories.find(x => x.Category == 'ALL').NotificationsCount++;
                }

                //Se agrega 1 al conteo de notificaciones del dropdown
                let categoryName = data.Notification.Category == 'CHAT' ? 'CHAT' : 'OTHERS';
                let targetDropdown = this.dropdowns.find(x => x.name == categoryName);
                targetDropdown.notificationsCount++;
                this.PulseBadge(targetDropdown);

                //se agrega uno al filtro del tipo de notificacion
                let notificationType = category.NotificationTypes.find(x => x.PKNotificationType == data.Notification.PKNotificationType);
                notificationType.Count++;

                //Se agrega la notificacion al listado de notificaciones del tipo de notificacion
                notificationType.notifications.unshift(notification);
            })
            .catch(error => {
                ShowNotification.Error(error, 'Ocurrió un error cargando una actualización')
            })
        });

        onUnmounted(unsub);

        // Cuando se obtienen nuevos mensajes
        let msgsChannel = 'newMessageNotificationsUpdate_' + this.store.CurrentOrganizationPK;
        if(process.env.VUE_APP_ENV != 'PRD')
            msgsChannel = process.env.VUE_APP_ENV + '_' + msgsChannel;

        const messagesQuery = query(
            collection(db, msgsChannel),
            orderBy('PKMessage', 'desc'),
            limit(1)
        );

        const messagesUnsub = onSnapshot(messagesQuery, async snapshot => {
            if(this.firstMessageNotification){
                this.firstMessageNotification = false;
                return;
            }

            let PKMessage = snapshot.docs[0].data().PKMessage;

            //Obtiene el expediente y usuario que envío el mensaje
            NotificationService.GetNotification({PKNotification : 'M' + PKMessage})
            .then(async data => {
                if(data.Notification == null){
                    return;
                }

                //Si el usuario que envió el mensaje es el mismo que el usuario logueado, no se agrega la notificación
                if(data.Notification.FKUser == this.store.LoggedUser.PKUser){
                    return;
                }

                //Si no se desea recibir notificaciones de ese expediente, no se agrega la notificación
                if(this.store.unpinnedFiles.includes(data.Notification.PKFile)){
                    return;
                }

                //Si el usuario se encuentra en la página de detalle de expediente, no se agrega la notificación
                let currentRoute = this.$router.currentRoute.value;
                if(currentRoute.name == 'fileDetail' && currentRoute.params.PKFile == data.Notification.PKFile){
                    return;
                }

                this.RefreshMessageNotifications();
                return;
            })
            .catch(error => {
                ShowNotification.Error(error, 'Ocurrió un error cargando una actualización')
            })

        });

        onUnmounted(messagesUnsub);

         // Llamada para forzar actualizacion de mensajes
        let notificationsUpdateChannel = 'messageNotificationsUpdateForce_' + this.store.CurrentOrganizationPK;
        if(process.env.VUE_APP_ENV != 'PRD')
            notificationsUpdateChannel = process.env.VUE_APP_ENV + '_' + notificationsUpdateChannel;

        const notificationUpdateQuey = query(
            collection(db, notificationsUpdateChannel),
            orderBy('Date', 'desc'),
            limit(1)
        );

        const notificationUpdateUnsub = onSnapshot(notificationUpdateQuey, async () => {
            if(this.firstNotificationsUpdate){
                this.firstNotificationsUpdate = false;
                return;
            }

            this.RefreshMessageNotifications({
                forcedUpdate: true
            });

        });

        onUnmounted(notificationUpdateUnsub);
    },

    beforeUnmount(){
        this.ClearObservers();
    },

    methods: {
        // Bump the badge's :key so it remounts and replays its pop keyframe —
        // a small, reliable attention cue each time a new notification lands.
        PulseBadge(dropdown){
            dropdown.pulseKey++;
        },

        FormatCount(count){
            if(count < 1000) return count;
            let k = count / 1000;
            let rounded = Math.floor(k * 10) / 10;
            return (rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)) + 'k';
        },

        BackToCategoriesClickHandler(){
            this.selectedCategoryPK = null;
            this.lastOtherSelectedCategoryPKPK = null;
        },

        // Type-filter summary. For the chat panel it's always the CHAT category —
        // decoupled from selectedCategoryPK on purpose: that value is set from
        // chatCategoryPK at open time and is still null when the menu is opened
        // before the categories finish loading, which used to leave the chat
        // chips hidden until a close/reopen. For the general panel it shows the
        // opened category, never ALL/TODAS (that one aggregates every type, so
        // its filter row is empty and adds no value).
        ShowCategorySummary(category, dropdown){
            if(dropdown.name == 'CHAT'){
                return category.Category == 'CHAT';
            }
            return category.PKNotificationCategory == this.selectedCategoryPK
                && category.Category !== 'ALL';
        },

        // Resolves whichever object is currently "in view" for a category: the
        // category itself, or — when a type chip is active — that type's own
        // feed object. Every getter below reads its field off this same result
        // instead of re-deriving the category-vs-type branch each time.
        GetActiveList(category){
            if(category.selectedTypePK == null){
                return category;
            }
            return category.NotificationTypes.find(x => x.PKNotificationType == category.selectedTypePK) ?? null;
        },

        // Resolves the notification list currently shown for a category: the
        // whole feed, or — when a type chip is active — that type's own feed.
        GetVisibleNotifications(category){
            let active = this.GetActiveList(category);
            return active ? active.notifications : [];
        },

        // Loading flag for whatever list is currently in view (category feed
        // or the active type feed).
        IsCategoryLoading(category){
            let active = this.GetActiveList(category);
            return active ? active.loading : false;
        },

        // Whether the visible list has reached its last page.
        IsEndOfList(category){
            let active = this.GetActiveList(category);
            return active ? active.endOfNotifications : false;
        },

        // First page still loading with nothing rendered yet -> show skeletons.
        IsInitialLoading(category){
            return this.GetVisibleNotifications(category).length === 0 && this.IsCategoryLoading(category);
        },

        // More to fetch (or a fetch in flight) -> show the inline spinner.
        ShowSpinner(category){
            return category.loading || !this.IsEndOfList(category);
        },

        // Nothing left to fetch and not loading -> show the end / empty state.
        ShowEndLabel(category){
            return !this.IsCategoryLoading(category) && this.IsEndOfList(category);
        },

        NotificationTypeChange(PKNotificationCategory, PKNotificationType)
        {
            let category = this.notificationCategories.find(x => x.PKNotificationCategory == PKNotificationCategory);
            category.selectedTypePK = PKNotificationType == category.selectedTypePK ? null : PKNotificationType;
            this.ResetCategoryObserver(category);
            
            //Regresa el oversroll y al tope
            let element = document.getElementById('notificationCategory_' + PKNotificationCategory);
            element.scrollTop = 0;
        },

        RefreshMessageNotifications(options = {
            forcedUpdate: false
        })
        {
            let category = this.notificationCategories.find(x => x.Category == 'CHAT');
            category.notifications = [];
            category.endOfNotifications = false;
            category.loading = false;
            category.PKLastNotification = null;

            if(!options.forcedUpdate){
                let chatDropdown = this.dropdowns.find(x => x.name == 'CHAT');
                chatDropdown.notificationsCount++;
                this.PulseBadge(chatDropdown);
            }

            let notificationType = category.NotificationTypes.find(x => x.NotificationType == 'SentMessage');
            notificationType.notifications = [];
            notificationType.endOfNotifications = false;
            notificationType.loading = false;

            this.ResetCategoryObserver(category);
        },

        ResetCategoryObserver(category)
        {
            category.loaderObserver.disconnect();
            this.ObserveFunction(category);
        },

        ClearObservers(){
            this.notificationCategories.forEach(category => {
                category.loaderObserver.disconnect();
            })
        },

        ObserveFunction(category)
        {
            const options = {
                rootMargin: '0px',
                threshold: 0.5
            };

            let parent = this.$refs['notificationLoader_' + category.PKNotificationCategory];
            if(!parent || parent.length == 0){
                return;
            }

            let targetElement = parent[0];
            let ogCategory = category;

            category.loaderObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if(category.selectedTypePK != null){
                        let notificationType = category.NotificationTypes.find(x => x.PKNotificationType == category.selectedTypePK);
                        category = notificationType;
                    }

                    if (entry.isIntersecting && !category.loading && !category.endOfNotifications){
                        category.loading = true;

                        //Loads notifications cargara las notificaciones y aplicara los filtros necesarios en la busqueda
                        this.LoadNotifications(ogCategory)
                        .then(()=>{
                            category.loading = false;

                            ogCategory.loaderObserver.unobserve(targetElement);
                            setTimeout(()=>{
                                ogCategory.loaderObserver.observe(targetElement);
                            }, 1500);
                        })
                        .catch(error => {
                            category.loading = false;
                            ShowNotification.Error(error, 'Ocurrió un error al cargar las notificaciones');
                        })
                    }
                });
            }, options);

            category.loaderObserver.observe(targetElement);
        },

        SetObservers()
        {
            this.notificationCategories.forEach(category => {
                this.ObserveFunction(category)
            });
        },

        ChangeNotificationCategory(category){
            this.selectedCategoryPK = category.PKNotificationCategory;
            this.lastOtherSelectedCategoryPKPK = category.PKNotificationCategory;
        },

        async ClearNotificationsByCategory(dropdown)
        {
            let auxCategoryPK =  dropdown.name == 'CHAT' ? this.chatCategoryPK : this.selectedCategoryPK;

            let data = {
                PKNotificationCategory: auxCategoryPK
            };

            this.store.SetLoader(true);
            await PromiseCallerService.CallAsPromise(NotificationService.ClearNotificationsByCategory, data)
            .then(data => {
                //Data regresa el nuevo conteo de notificaciones
                let category = this.notificationCategories.find(x => x.PKNotificationCategory == auxCategoryPK);

                if(category.Category == 'ALL'){
                    // Se marca IsRead todas las notificaciones de las categorías que no sean chat
                    this.notificationCategories.filter(x => x.Category != 'CHAT').forEach(x => { 
                        x.notifications.map(x => x.IsRead = true);
                        x.NotificationsCount = 0;
                    });

                    // Se limpian las notificaciones sin leer de los diferentes tipos de notificaciones de cada categoria
                    this.notificationCategories.forEach(x => x.NotificationTypes.forEach(y => {
                        y.notifications.map(z => z.IsRead = true);
                        y.Count = 0;
                    }));
                }
                else{
                    // Se marcan como leidas las notificaciones de la cateogria
                    category.notifications.filter(x => !x.IsRead).forEach(x => x.IsRead = true);

                    if(category.Category != 'CHAT'){
                        // Se marcan como leidas las notificaciones de la categoria dentro de ALL
                        let allCategory = this.notificationCategories.find(x => x.Category == 'ALL');
                        let notificationsToSetAsReadInAll = allCategory.notifications.filter(x => category.notifications.some(y => y.PKNotification == x.PKNotification));
                        notificationsToSetAsReadInAll.forEach(x => x.IsRead = true);
                    }
                    
                    // Se limpian las notificaciones sin leer de la categoria
                    category.NotificationTypes.forEach(x => {
                        x.notifications.map(x => x.IsRead = true);
                        x.Count = 0;
                    });
                }

                //Se recalcula la cantidad de notificaciones por categoria
                data.newNotificationsCount.forEach(x => {
                    let category = this.notificationCategories.find(y => y.PKNotificationCategory == x.PKNotificationCategory);
                    category.NotificationsCount = x.Count;
                });
                
                ShowNotification.Success('Las notificaciones han sido marcadas como leídas exitosamente');
            })
            .catch(error => {
                ShowNotification.Error(error, 'Ocurrió un error al marcar las notificaciones como leidas');
            })
            this.store.SetLoader(false);
        },

        GetNotificationCategories(){
            this.categoriesLoading = true;
            PromiseCallerService.CallAsPromise(NotificationService.GetNotificationCategories)
            .then(data => {
                data.NotificationCategories.forEach(category => {
                    category.notifications = [];
                    category.endOfNotifications = false;
                    category.loading = false;
                    category.PKLastNotification = null;
                    category.loaderObserver = null;
                    category.selectedTypePK = null;
                    
                    category.NotificationTypes.forEach(x => {
                        x.notifications = [];
                        x.endOfNotifications = false;
                        x.loading = false;
                        x.PKLastNotification = null;
                    });
                });

                this.notificationCategories = data.NotificationCategories;


                this.dropdowns.find(x => x.name == 'OTHERS').notificationsCount = this.notificationCategories.find(x => x.Category == 'ALL').NotificationsCount;
                this.dropdowns.find(x => x.name == 'CHAT').notificationsCount = this.notificationCategories.find(x => x.Category == 'CHAT').NotificationsCount;

                this.chatCategoryPK = this.notificationCategories.find(x => x.Category == 'CHAT').PKNotificationCategory;

                this.categoriesLoading = false;

                this.$nextTick(() => {
                    this.SetObservers();
                });
            })
            .catch(error => {
                this.categoriesLoading = false;
                ShowNotification.Error(error, 'Ocurrió un error al cargar las categorías de notificaciones');
            })
        },

        // Category = CATEGORIA PADRE (PUEDE SER CHAT O ALL)
        ClickHandler(category, notification) {
            if(!notification.IsRead){
                // Obtiene las categorias
                let allCategory = this.notificationCategories.find(x => x.Category == 'ALL');
                let notificationCategory = this.notificationCategories.find(x => x.PKNotificationCategory == notification.PKNotificationCategory);
                let notificationType = notificationCategory.NotificationTypes.find(x => x.PKNotificationType == notification.PKNotificationType);

                let allNotifications = [];
                let categoryNotifications = [];
                let typeNotifications = [];

                let isConversationNotification = notification.PKNotification == 0;

                // Si es de tipo conversación, se marcan leidas por FKFile (porque no tienen PKNotification)
                if(isConversationNotification){
                    categoryNotifications = category.notifications.filter(x => x.PKFile == notification.PKFile && x.PKNotificationType == notification.PKNotificationType);
                    typeNotifications = notificationType.notifications.filter(x => x.PKFile == notification.PKFile);
                }
                // Si no, se marcan leidas por PKNotification
                else{
                    allNotifications = allCategory.notifications.filter(x => x.PKNotification == notification.PKNotification);
                    categoryNotifications = notificationCategory.notifications.filter(x => x.PKNotification == notification.PKNotification);
                    typeNotifications = notificationType.notifications.filter(x => x.PKNotification == notification.PKNotification);
                }
                // Se marcan las notificaciones como leidas en ALL, Category y Type
                let notificationsToSetAsRead = categoryNotifications.concat(typeNotifications).concat(allNotifications);
                notificationsToSetAsRead.forEach(x => x.IsRead = true);

                // Si no es mensaje, se resta de ALL
                if(!isConversationNotification){
                    allCategory.NotificationsCount--;
                }

                notificationCategory.NotificationsCount--;
                notificationType.Count--;

                //Se marca la notificación como leida en la BD
                if(notification.PKNotification != 0){
                    NotificationService.SetNotificationRead({PKNotification: notification.PKNotification})
                    .catch(error =>{
                        ShowNotification.Error(error, 'Ocurrió un error al marcar la notificación como leída');
                    });
                }
            }

            //Closes the notification dropdown in mobile when is clicked
            if(category.Category == 'CHAT'){
                this.$refs['notificationButton_CHAT'][0].click();
            }
            else{
                this.$refs['notificationButton_OTHERS'][0].click();
            }

            //Se redirige a la página de detalle del expediente de la notificación
            const PKCurrentFile = this.$route.params.PKFile;
            if(PKCurrentFile != notification.PKFile){
                this.store.notificationRedirectPK = notification.PKFile;
                this.router.push({ name: 'fileDetail', params: { PKFile: notification.PKFile }});

                this.$nextTick(() => {
                    EventBus.$emit('RefreshFilePage');
                });
            }
        },

        NotificationButtonHandler(dropdown){
            this.selectedCategoryPK = dropdown.name == 'CHAT' ? this.chatCategoryPK : this.lastOtherSelectedCategoryPKPK;
            dropdown.notificationsCount = 0;
        },

        LoadNotifications(category){
            return new Promise((resolve, reject) => {

                let data = {};
                let isChat = false;

                if(category.selectedTypePK != null){
                    data.PKNotificationCategory = category.PKNotificationCategory;
                    category = category.NotificationTypes.find(x => x.PKNotificationType == category.selectedTypePK);
                    data.PKNotificationType = category.PKNotificationType;
                    data.PKLastNotification = category.PKLastNotification;

                    isChat = category.NotificationType == 'SentMessage';
                    if(isChat)
                        data.rowNumber = category.notifications.length;
                }
                else{
                    data.PKNotificationCategory = category.PKNotificationCategory;
                    data.PKLastNotification = category.PKLastNotification;
                    data.rowNumber = category.notifications.length;

                    isChat = category.Category == 'CHAT';
                }

                let func = isChat ? NotificationService.GetMessagesNotifications : NotificationService.GetNotificationsByCategory;

                func(data)
                .then(async data => {
                    let notifications = data.Notifications;
                    category.endOfNotifications = notifications.length < 25;

                    let removeExistingNotifications = !isChat && category.PKLastNotification == null;

                    if(data.Notifications.length > 0){
                        if(!isChat)
                            category.PKLastNotification = data.Notifications[data.Notifications.length - 1].PKNotification;
                        else{
                            //Obtiene las notificaciones que NO TENGAN PKNotification = 0
                            let notificationsToSetAsRead = data.Notifications.filter(x => x.PKNotification != 0);

                            //Obtiene el menor PKNotification de esos
                            let PKLastNotification = Math.min(...notificationsToSetAsRead.map(x => x.PKNotification));

                            //Se actualiza el PKLastNotification
                            if(isFinite(PKLastNotification))
                                category.PKLastNotification = PKLastNotification;

                            //Se actualiza la cantidad de unread conversations type
                            if(category.NotificationTypes){
                                let sentMessageCategory = category.NotificationTypes.find(x => x.NotificationType == 'SentMessage');
                                sentMessageCategory.Count = data.UnreadMessagesCount;
                            }
                            else{
                                category.Count = data.UnreadMessagesCount;
                            }
                        }
                    }

                    await this.GetNewUsersData(notifications);

                    if(removeExistingNotifications){
                        category.notifications = [];
                    }
                    
                    category.notifications = category.notifications.concat(notifications);
                    category.loading = false;

                    resolve();
                })
                .catch(error => {
                    category.loading = false;
                    reject(error);
                })
            })
        },

        GetNewUsersData(newNotifications) {
            return new Promise((resolve, reject) => {
                //getting the distintc senders name and profile picture
                let usersPKs = [];
                    
                //get the FK of the sender in the new messages
                usersPKs = [...new Set(newNotifications.map(x => x.FKUser))];

                //remove those who are already in the users array
                usersPKs = usersPKs.filter(x => !this.users[x]);

                if(usersPKs.length == 0){
                    resolve();
                    return;
                }

                //In case a different user sent a message gets it profile picture
                this.GetUsersMessageData(usersPKs)
                .then(() => {
                    resolve();
                })
                .catch(error => {
                    reject(error);
                })
            });
        },

        GetUsersMessageData(usersPKs) {
            return new Promise((resolve, reject) => {
                UserService.GetUsersMessageData({ usersPKs: usersPKs })
                .then(data => {
                    data.Users.forEach(user => {
                        this.store.loadedUsersData[user.PKUser] = user;
                        this.users[user.PKUser] = user;
                    });

                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
            });
        }
    }
}
</script>

<style scoped>
/* ---------- Local tokens (mirror the redesigned navbar / login world) ---------- */
.dropdown {
    /* Raisin bar, lavender as the single signature accent. Values come from
       the shared dropdown-panel tokens in App.vue so this panel can't drift
       out of sync with the profile panel it mirrors. */
    --nm-accent: var(--LativoLightPurple);
    --nm-ink: var(--DropdownPanelInk);
    --nm-ink-dim: var(--DropdownPanelInkDim);
    --nm-ink-faint: var(--DropdownPanelInkFaint);
    --nm-hover: var(--DropdownPanelHover);
    --nm-surface: var(--DropdownPanelSurface);
    --nm-line: var(--DropdownPanelLine);
    --nm-ease: var(--DropdownPanelEase);

    max-width: 200px;
}

.dropdown.chat {
    margin-left: auto;
}

/* ---------- Panel ---------- */
/* Positioning, entrance animation, background/border/shadow, and z-index come
   from the shared .staticDropdownPanel utility (App.vue) — only this panel's
   own size/spacing stays local. */
.notificationMenu {
    margin-top: 8px;
    width: 400px;
    max-width: calc(100vw - 16px);
    height: auto;
    padding: 0;
    border-color: var(--nm-line);
    font-size: 14px !important;
}

/* ---------- Header ---------- */
.nm-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: var(--LativoDarkPurple);
}

.nm-header__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 12px 6px 16px;
}

.nm-header__title {
    flex: 1;
    min-width: 0;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 14px;
    /* Sentence case: this is the panel's primary heading and is often a full
       phrase — uppercase hurt its legibility. Tracking goes slightly negative,
       correct for mixed-case titles. */
    letter-spacing: -0.01em;
    color: var(--nm-ink) !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.nm-header__clear {
    margin-left: auto;
}

/* Header icon buttons — same rounded-wash language as the navbar action icons. */
.nm-iconBtn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background-color: transparent;
    border: none;
    border-radius: 9px;
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, transform 140ms var(--nm-ease);
    -webkit-tap-highlight-color: transparent;
}

.nm-iconBtn i {
    font-size: 15px;
    color: inherit;
    margin: 0;
}

.nm-iconBtn:active {
    transform: scale(0.92);
}

@media (hover: hover) and (pointer: fine) {
    .nm-iconBtn:hover {
        background-color: var(--nm-hover);
        color: #ffffff;
    }
}

/* ---------- Category picker ---------- */
.nm-categories {
    padding: 4px 8px 8px;
}

.nm-category {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    background-color: transparent;
    border: none;
    border-radius: 11px;
    color: var(--nm-ink);
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    transition: background-color 160ms ease;
    -webkit-tap-highlight-color: transparent;
}

.nm-category__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 10px;
    background-color: var(--nm-surface);
    color: var(--nm-accent);
    font-size: 15px;
}

.nm-category__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.nm-category__count {
    flex-shrink: 0;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background-color: rgba(222, 228, 255, 0.14);
    color: var(--nm-accent);
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 12px;
    line-height: 1;
}

.nm-category__chevron {
    flex-shrink: 0;
    font-size: 12px;
    color: var(--nm-ink-faint);
    transition: transform 200ms var(--nm-ease);
}

.nm-category:active {
    background-color: var(--nm-hover);
    transform: scale(0.99);
}

/* Keyboard focus — shared across the panel's interactive controls, tied to
   the same lavender accent the navbar uses. */
.nm-iconBtn:focus-visible,
.nm-category:focus-visible,
.nm-chip:focus-visible {
    outline: 2px solid var(--nm-accent);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .nm-category:hover {
        background-color: var(--nm-hover);
    }

    .nm-category:hover .nm-category__chevron {
        transform: translateX(3px);
        color: var(--nm-accent);
    }
}

/* ---------- Type-filter summary ---------- */
.nm-summary {
    padding: 2px 14px 12px;
    border-bottom: 1px solid var(--nm-line);
}

.nm-summary__title {
    margin: 8px 2px 10px;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 12px;
    letter-spacing: 0.02em;
    color: var(--nm-ink-dim);
}

.nm-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.nm-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 12px 5px 6px;
    background-color: var(--nm-surface);
    border: 1px solid transparent;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 12px;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease;
    -webkit-tap-highlight-color: transparent;
}

.nm-chip__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border-radius: 50%;
    background-color: rgba(222, 228, 255, 0.16);
    color: var(--nm-accent);
    font-size: 12px;
    transition: background-color 160ms ease, color 160ms ease;
}

.nm-chip__label {
    line-height: 1;
}

.nm-chip:active {
    transform: scale(0.97);
}

@media (hover: hover) and (pointer: fine) {
    .nm-chip:hover {
        background-color: var(--nm-hover);
        color: #ffffff;
        border-color: var(--nm-line);
    }
}

/* Selected chip: lavender fill + raisin ink — the world's active-state language. */
.nm-chip--selected {
    background-color: var(--nm-accent);
    color: var(--LativoDarkPurple);
    font-family: 'SwitzerMedium', sans-serif;
}

.nm-chip--selected .nm-chip__icon {
    background-color: rgba(31, 31, 44, 0.14);
    color: var(--LativoDarkPurple);
}

@media (hover: hover) and (pointer: fine) {
    .nm-chip--selected:hover {
        background-color: var(--nm-accent);
        color: var(--LativoDarkPurple);
        border-color: transparent;
        filter: brightness(1.03);
    }
}

/* ---------- List ---------- */
.notificationsWrapper {
    max-height: 460px;
    overflow-y: auto;
    padding: 6px 0;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: rgba(222, 228, 255, 0.2) transparent;
}

.notificationsWrapper::-webkit-scrollbar {
    width: 8px;
}

.notificationsWrapper::-webkit-scrollbar-thumb {
    background-color: rgba(222, 228, 255, 0.18);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
}

.notificationsWrapper::-webkit-scrollbar-thumb:hover {
    background-color: rgba(222, 228, 255, 0.3);
}

/* ---------- Badge on the trigger ---------- */
.notification {
    /* Anchored at a fixed % from the button's left and grown rightward, so the
       badge always sits just above the glyph and never creeps over it — no
       matter how long the count string gets. The icon stays perfectly centered
       because the badge is out of flow (absolute). */
    position: absolute;
    top: -3px;
    left: 55%;
    right: auto;
    z-index: 100;
    min-width: 17px;
    height: 17px;
    padding: 0 4px;
    box-sizing: border-box;
    border-radius: 9px;
    background-color: #ff4d4f;
    color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    /* Ring in the bar colour keeps the badge legible over the icon glyph. */
    box-shadow: 0 0 0 2px var(--LativoDarkPurple);
    /* Scale from the anchored corner so the pop grows outward, not over the icon. */
    transform-origin: top left;
    /* Remounted via :key on every new notification — the overshoot reads both as
       an entrance and as the "you got one" attention cue (occasional). */
    animation: notificationPop 320ms var(--nm-ease);
}

@keyframes notificationPop {
    0%   { transform: scale(0.5); opacity: 0; }
    55%  { transform: scale(1.18); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
}

/* ---------- Loader / spinner ---------- */
.notificationLoaderWrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 6px 16px 16px;
}

.notificationLoaderWrapper .notificationLoader {
    display: block;
    width: 1px;
    height: 1px;
    background-color: transparent;
}

/* Three lavender dots easing up and down in sequence — quieter than a ring. */
.nm-spinner {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 12px 0 4px;
}

.nm-spinner span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--nm-accent);
    opacity: 0.5;
    animation: nm-load-dot 900ms var(--nm-ease) infinite;
}

.nm-spinner span:nth-child(2) { animation-delay: 150ms; }
.nm-spinner span:nth-child(3) { animation-delay: 300ms; }

@keyframes nm-load-dot {
    0%, 100% { transform: translateY(0); opacity: 0.35; }
    50%      { transform: translateY(-5px); opacity: 1; }
}

/* ---------- End / empty states ---------- */
.nm-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
    padding: 10px 0 4px;
}

.nm-empty__rule {
    width: 40px;
    height: 1px;
    margin-bottom: 10px;
    background-color: var(--nm-line);
}

.nm-empty__text {
    font-size: 12px;
    letter-spacing: 0.02em;
    color: var(--nm-ink-faint);
}

.nm-empty__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin: 14px 0 12px;
    border-radius: 14px;
    background-color: var(--nm-surface);
    color: var(--nm-accent);
    font-size: 19px;
}

.nm-empty__title {
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 14px;
    color: var(--nm-ink);
    margin-bottom: 4px;
}

.nm-empty__hint {
    font-size: 12.5px;
    line-height: 1.4;
    color: var(--nm-ink-dim);
    max-width: 32ch;
    margin-bottom: 8px;
}

/* ---------- Skeletons ---------- */
.nm-skeletonList {
    padding: 2px 0;
}

.nm-skeleton {
    display: flex;
    align-items: center;
    gap: 13px;
    padding: 11px 20px;
}

.nm-skeleton__avatar {
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    border-radius: 50%;
}

.nm-skeleton__lines {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.nm-skeleton__line {
    height: 9px;
    border-radius: 5px;
}

.nm-skeleton__line--xs { width: 28%; }
.nm-skeleton__line--sm { width: 42%; }
.nm-skeleton__line--lg { width: 82%; }

/* Category-picker skeleton: mirrors the .nm-category row footprint. */
.nm-catSkeleton {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
}

.nm-catSkeleton__icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 10px;
}

.nm-catSkeleton__label {
    height: 10px;
    width: 55%;
    border-radius: 5px;
}

/* Vary the label widths so the stack reads as content, not a grid. */
.nm-catSkeleton:nth-child(2) .nm-catSkeleton__label { width: 62%; }
.nm-catSkeleton:nth-child(3) .nm-catSkeleton__label { width: 44%; }
.nm-catSkeleton:nth-child(4) .nm-catSkeleton__label { width: 58%; }
.nm-catSkeleton:nth-child(5) .nm-catSkeleton__label { width: 48%; }

/* Type-chip skeleton: a pill matching the .nm-chip footprint. */
.nm-chipSkeleton {
    height: 30px;
    border-radius: 999px;
}

.nm-chipSkeleton:nth-child(1) { width: 88px; }
.nm-chipSkeleton:nth-child(2) { width: 108px; }
.nm-chipSkeleton:nth-child(3) { width: 76px; }

/* Shared shimmer surface: a lavender sheen sweeping over a faint base. */
.nm-skeleton__avatar,
.nm-skeleton__line,
.nm-catSkeleton__icon,
.nm-catSkeleton__label,
.nm-chipSkeleton {
    background: linear-gradient(
        100deg,
        rgba(222, 228, 255, 0.05) 30%,
        rgba(222, 228, 255, 0.13) 50%,
        rgba(222, 228, 255, 0.05) 70%
    );
    background-size: 200% 100%;
    animation: nm-shimmer 1.4s ease-in-out infinite;
}

@keyframes nm-shimmer {
    from { background-position: 200% 0; }
    to   { background-position: -200% 0; }
}

/* Reveal: each category row eases up into place once loaded. Staggered via
   the per-row --nm-enter-delay so the list resolves in sequence, not all
   at once. Keyframes (not a transition) — this plays exactly once on mount. */
.nm-category--enter {
    animation: nm-category-enter 260ms var(--nm-ease) both;
    animation-delay: var(--nm-enter-delay, 0ms);
}

@keyframes nm-category-enter {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
}

/* Reveal: type chips ease up into place once the types resolve. Plays once on
   mount (keyframes, not a transition) — display-toggling the summary later
   won't replay it. Staggered via the per-chip --nm-enter-delay. */
.nm-chip--enter {
    animation: nm-chip-enter 220ms var(--nm-ease) both;
    animation-delay: var(--nm-enter-delay, 0ms);
}

@keyframes nm-chip-enter {
    from { opacity: 0; transform: translateY(5px); }
    to   { opacity: 1; transform: translateY(0); }
}

/* ---------- Mobile: full-width sheet, pinned under the navbar ---------- */
@media screen and (max-width: 915px) {
    /* Fixed to the viewport (not anchored to the tiny trigger) so it spans the
       full width exactly — no right-side spill, no horizontal scroll. */
    .notificationMenu {
        position: fixed !important;
        top: 60px !important;
        left: 0 !important;
        right: 0 !important;
        margin-top: 0;
        width: 100%;
        max-width: 100%;
        max-height: calc(100dvh - 60px);
        border-radius: 0 0 16px 16px;
        border-left: none;
        border-right: none;
        border-bottom: 1px solid var(--nm-line);
    }

    /* Sheet drops straight down from the bar, not out of a corner. */
    .notificationMenu.show {
        transform-origin: top center;
    }

    .notificationsWrapper {
        max-height: calc(100dvh - 200px);
    }
}

/* ---------- Reduced motion ---------- */
@media (prefers-reduced-motion: reduce) {
    .notification {
        animation: none;
    }

    .nm-spinner span,
    .nm-skeleton__avatar,
    .nm-skeleton__line,
    .nm-catSkeleton__icon,
    .nm-catSkeleton__label,
    .nm-chipSkeleton {
        animation: none;
    }

    .nm-skeleton__avatar,
    .nm-skeleton__line,
    .nm-catSkeleton__icon,
    .nm-catSkeleton__label,
    .nm-chipSkeleton {
        background: rgba(222, 228, 255, 0.09);
    }

    /* Keep categories and chips legible on arrival — no upward motion. */
    .nm-category--enter,
    .nm-chip--enter {
        animation: none;
    }

    .nm-iconBtn:active,
    .nm-chip:active,
    .nm-category:active {
        transform: none;
    }

    .nm-category:hover .nm-category__chevron {
        transform: none;
    }
}
</style>